import { useContext, useEffect, useRef } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { gsap } from 'gsap'
import { ThemeContext } from './contexts/theme'
import Header from './components/Header/Header'
import Footer from './components/Footer/Footer'
import CustomCursor from './components/CustomCursor/CustomCursor'
import SmoothScroll from './components/SmoothScroll/SmoothScroll'
import HomePage from './pages/HomePage'
import BlogList from './pages/BlogList/BlogList'
import BlogPost from './pages/BlogPost/BlogPost'
import ProjectList from './pages/ProjectList/ProjectList'
import ProjectDetail from './pages/ProjectDetail/ProjectDetail'
import Dashboard from './pages/Dashboard/Dashboard'
import HiHomePage from './pages/HiHomePage/HiHomePage'
import { captureVisitorFromUrl, captureUtmParams } from './lib/visitorContext'
import { logPageView, initSessionTracking } from './supabaseTracker'

import './App.css'


const App = () => {
  const [{ themeName }] = useContext(ThemeContext)
  const clickSoundRef = useRef(null)
  const location = useLocation()
  const overlayRef = useRef(null)

  useEffect(() => {
    // Prevent right-click context menu
    const handleContextMenu = (e) => {
      e.preventDefault()
      return false
    }

    // Prevent copy, cut, paste, and select all shortcuts
    const handleKeyDown = (e) => {
      // Ctrl+C, Ctrl+X, Ctrl+V, Ctrl+A, Ctrl+S, Ctrl+P, F12
      if (
        (e.ctrlKey && (e.key === 'c' || e.key === 'x' || e.key === 'v' || e.key === 'a' || e.key === 's' || e.key === 'p')) ||
        // e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C'))
      ) {
        e.preventDefault()
        return false
      }
    }

    // Prevent text selection via mouse drag
    const handleSelectStart = (e) => {
      e.preventDefault()
      return false
    }

    // Prevent drag
    const handleDragStart = (e) => {
      e.preventDefault()
      return false
    }

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu)
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('selectstart', handleSelectStart)
    document.addEventListener('dragstart', handleDragStart)

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu)
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('selectstart', handleSelectStart)
      document.removeEventListener('dragstart', handleDragStart)
    }
  }, [])

  // Play click sound on user clicks
  useEffect(() => {
    // lazy init to avoid creating Audio before hydration
    if (!clickSoundRef.current) {
      clickSoundRef.current = new Audio('/audio/mouse-click.wav')
      clickSoundRef.current.preload = 'auto'
      clickSoundRef.current.volume = 0.55
    }

    const handleClickSound = () => {
      const a = clickSoundRef.current
      if (!a) return
      try {
        a.currentTime = 0
        const p = a.play()
        if (p && typeof p.catch === 'function') p.catch(() => {})
      } catch (_) {}
    }

    document.addEventListener('click', handleClickSound, { passive: true })
    return () => document.removeEventListener('click', handleClickSound)
  }, [])

  // Support hash navigation across routes (e.g. /projects -> /#skills)
  useEffect(() => {
    const hash = location.hash
    if (!hash) return

    const id = hash.replace('#', '')

    const headerOffset = 90
    const maxWaitMs = 1200
    const start = performance.now()
    let rafId = 0

    // Give the route a moment to paint before scrolling (prevents “instant snap”)
    const minDelayMs = 380

    const tick = () => {
      const el = document.getElementById(id)
      if (el) {
        if (performance.now() - start < minDelayMs) {
          rafId = window.requestAnimationFrame(tick)
          return
        }

        const top =
          el.getBoundingClientRect().top + window.pageYOffset - headerOffset

        const lenis = window.__lenis
        if (lenis && typeof lenis.scrollTo === 'function') {
          lenis.scrollTo(top, { duration: 1.15, immediate: false })
        } else {
          window.scrollTo({ top, behavior: 'smooth' })
        }
        return
      }

      if (performance.now() - start > maxWaitMs) return
      rafId = window.requestAnimationFrame(tick)
    }

    // Start on next frame to allow mount/layout
    rafId = window.requestAnimationFrame(tick)

    return () => window.cancelAnimationFrame(rafId)
  }, [location.pathname, location.hash])

  useEffect(() => {
    initSessionTracking()
  }, [])

  useEffect(() => {
    captureUtmParams()
    captureVisitorFromUrl(location.search)
    logPageView(`${location.pathname}${location.search}${location.hash}`).catch(() => {})
  }, [location.pathname, location.search, location.hash])

  // Route transition overlay: allows Navbar to trigger a non-abrupt change
  useEffect(() => {
    window.__routeTransition = {
      play: () =>
        new Promise((resolve) => {
          const el = overlayRef.current
          if (!el) return resolve()
          gsap.killTweensOf(el)
          gsap.set(el, { pointerEvents: 'auto' })
          gsap.to(el, {
            opacity: 1,
            duration: 0.28,
            ease: 'power3.out',
            onComplete: resolve,
          })
        }),
      clear: () =>
        new Promise((resolve) => {
          const el = overlayRef.current
          if (!el) return resolve()
          gsap.killTweensOf(el)
          gsap.to(el, {
            opacity: 0,
            duration: 0.44,
            ease: 'power3.out',
            onComplete: () => {
              gsap.set(el, { pointerEvents: 'none' })
              resolve()
            },
          })
        }),
    }

    return () => {
      if (window.__routeTransition) delete window.__routeTransition
    }
  }, [])

  // Clear overlay after route mounts (so the user sees a smooth reveal)
  useEffect(() => {
    const t = window.setTimeout(() => {
      const rt = window.__routeTransition
      if (rt && typeof rt.clear === 'function') rt.clear()
    }, 60)
    return () => window.clearTimeout(t)
  }, [location.pathname, location.hash])

  return (
    <SmoothScroll>
      <div className={`${themeName} app`}>
        <CustomCursor />
        <Header />
        <div className="route-overlay" ref={overlayRef} />

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blogs" element={<BlogList />} />
          <Route path="/blogs/:slug" element={<BlogPost />} />
          <Route path="/projects" element={<ProjectList />} />
          <Route path="/projects/:slug" element={<ProjectDetail />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/hi/:visitorName" element={<HiHomePage />} />
        </Routes>

        <Footer />
      </div>
    </SmoothScroll>
  )
}

export default App
